import {
  AuthFields,
  AuthHeading,
  AuthNote,
  AuthShell,
  AuthStack,
  authActionClass,
  Button,
  TextButton,
} from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Input } from "@majitap/ui/arc/input/input";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { type FormEvent, type ReactNode, useState } from "react";
import { emailProblem, type Problem, signInProblem } from "../auth-copy";
import { isAuthError, requestPasswordReset } from "../session";
import { HELP_LINE } from "./sign-in";

/**
 * Forgot password: a link to choose a new one, by email. The answer is the same whether or not
 * the email is staff's, so the page never says which.
 */
export function ForgotPasswordPage(): ReactNode {
  const search = useSearch({ from: "/forgot-password" });
  const navigate = useNavigate();
  const [email, setEmail] = useState(search.email ?? "");
  const [emailError, setEmailError] = useState<string>();
  const [problem, setProblem] = useState<Problem>({});
  const [sentTo, setSentTo] = useState<string>();
  const [pending, setPending] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const bad = emailProblem(email);
    setEmailError(bad);
    if (bad) return;

    setPending(true);
    setProblem({});
    try {
      await requestPasswordReset(email.trim());
      setSentTo(email.trim());
    } catch (error) {
      setProblem(signInProblem(isAuthError(error) ? error.code : "UNKNOWN", ""));
    }
    setPending(false);
  };

  const back = (
    <AuthNote>
      <TextButton onClick={() => navigate({ to: "/sign-in" })}>Back to sign in</TextButton>
    </AuthNote>
  );

  if (sentTo) {
    return (
      <AuthShell product="Admin console" helpLine={HELP_LINE}>
        <AuthStack>
          <AuthHeading title="Check your email" />
          <Alert tone="success" title="Link sent">
            {`If ${sentTo} has access to the console, it now has a link to choose a new password. The link works for an hour.`}
          </Alert>
          <AuthNote>Nothing after a few minutes? Check your spam folder, or ask an administrator to send one.</AuthNote>
          {back}
        </AuthStack>
      </AuthShell>
    );
  }

  return (
    <AuthShell product="Admin console" helpLine={HELP_LINE}>
      <form onSubmit={submit} noValidate>
        <AuthStack>
          <AuthHeading
            title="Reset your password"
            description="Enter your work email. We'll send you a link to choose a new password."
          />
          {problem.alert ? (
            <Alert tone="danger" title={problem.alert.title}>
              {problem.alert.body}
            </Alert>
          ) : null}
          <AuthFields>
            <Input
              label="Work email"
              type="email"
              name="email"
              autoComplete="username"
              inputMode="email"
              description="The address you sign in with."
              error={emailError}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </AuthFields>
          <Button type="submit" variant="primary" className={authActionClass} loading={pending}>
            Send the link
          </Button>
          {back}
        </AuthStack>
      </form>
    </AuthShell>
  );
}
